import { useEffect, useState } from 'react'
import { IconChevronRight, IconLock } from '@tabler/icons-react'
import type { AuditChangeEvent } from '@behuman/core-contract'
import clsx from 'clsx'
import { ActorAvatar, ChangeSourceBadge } from './AuditBadges'
import {
  AUDIT_INTEGRITY_STATEMENTS,
  changeActorLabel,
  changeEventTitle,
  changeFieldLabel,
  changeMetadataEntries,
  formatAuditDate,
  formatAuditTime,
  changeRowSubline,
} from './auditModel'

type Props = {
  events: AuditChangeEvent[]
}

/**
 * Change History per mockup 23673: a list of change rows on the left and a
 * CHANGE DETAILS panel on the right with red/green before/after blocks.
 */
export function ChangeHistoryTab({ events }: Props) {
  const [selectedIndex, setSelectedIndex] = useState(0)

  // Keep the selection valid when filters shrink the list.
  useEffect(() => {
    if (selectedIndex >= events.length) setSelectedIndex(0)
  }, [events.length, selectedIndex])

  if (events.length === 0) {
    return (
      <div className="py-12 text-center text-sm text-gray-500">
        No change events recorded for the selected filters.
      </div>
    )
  }

  const selected = events[selectedIndex] ?? events[0]

  return (
    // QA round 3 (re-test) — the two panes sit SIDE BY SIDE only once there is room for
    // both. The detail panel is a fixed 420px that never shrinks, so between roughly
    // 830px and 1000px it took the whole row and left the list a few pixels wide: every
    // activity title was clipped away and the rows showed nothing but an avatar and a
    // timestamp. Below the breakpoint the list gets the full width and the detail panel
    // stacks under it, which keeps both readable; at desktop widths the layout is
    // unchanged.
    <div className="flex flex-col lg:flex-row">
      <div className="min-w-0 flex-1 divide-y divide-gray-100 border-b border-gray-200 lg:border-r lg:border-b-0">
        {events.map((event, index) => (
          <button
            key={`${event.resourceId}-${event.versionId ?? index}-${event.field}`}
            type="button"
            data-testid="change-event-row"
            onClick={() => setSelectedIndex(index)}
            className={clsx(
              'flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-gray-50',
              index === selectedIndex && 'border-l-2 border-teal-600 bg-teal-50/50',
            )}
          >
            <ActorAvatar name={event.actorName ?? 'System'} />
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline gap-2">
                <span className="truncate text-sm font-semibold text-gray-900">
                  {changeEventTitle(event)}
                </span>
                <span className="shrink-0 text-xs text-gray-400">
                  {formatAuditDate(event.recordedAt)} · {formatAuditTime(event.recordedAt)}
                </span>
              </div>
              <div className="truncate text-xs text-gray-500">{changeRowSubline(event)}</div>
              <div className="truncate text-xs text-gray-400">{changeActorLabel(event)}</div>
            </div>
            <ChangeSourceBadge source={event.source} />
            <IconChevronRight size={16} className="shrink-0 text-gray-400" />
          </button>
        ))}
      </div>

      <div className="w-full p-5 lg:w-[420px] lg:shrink-0" data-testid="change-detail-panel">
        <div className="text-xs font-semibold tracking-wide text-gray-500 uppercase">
          Change details
        </div>
        <h3 className="mt-1 text-lg font-semibold text-gray-900">{changeEventTitle(selected)}</h3>

        <div className="mt-4 grid grid-cols-2 gap-4">
          <div>
            <div className="text-xs font-semibold tracking-wide text-gray-500 uppercase">
              Changed by
            </div>
            {/* Name AND role: attribution falls back to the version's meta.author, which is
                usually a ClientApplication, and "Middleware" alone would read as a person. */}
            <div className="mt-1 text-sm text-gray-900">{changeActorLabel(selected)}</div>
          </div>
          <div>
            <div className="text-xs font-semibold tracking-wide text-gray-500 uppercase">
              Date and time
            </div>
            <div className="mt-1 text-sm text-gray-900">
              {formatAuditDate(selected.recordedAt)}, {formatAuditTime(selected.recordedAt)}
            </div>
          </div>
          <div>
            <div className="text-xs font-semibold tracking-wide text-gray-500 uppercase">Field</div>
            <div className="mt-1 text-sm text-gray-900">{changeFieldLabel(selected.field)}</div>
          </div>
          {/* The changed record's stable reference. The row title now carries its NAME
              (see changeRecordTitle), which is what tells two documents of one type apart
              at a glance; this is the identifier that survives a rename, for a reader who
              needs to go find the record itself. Same reasoning as Actor ID above. */}
          <div data-testid="change-record-reference">
            <div className="text-xs font-semibold tracking-wide text-gray-500 uppercase">
              Record
            </div>
            <div className="mt-1 text-sm break-all text-gray-900">
              {selected.resourceType}/{selected.resourceId}
            </div>
          </div>
          {/* S20 — the ticket's remaining per-change metadata. Only what the FHIR record
              actually carries is shown; see changeMetadataEntries. */}
          {changeMetadataEntries(selected).map(([label, value]) => (
            <div key={label} data-testid="change-metadata-field">
              <div className="text-xs font-semibold tracking-wide text-gray-500 uppercase">
                {label}
              </div>
              <div className="mt-1 text-sm break-words text-gray-900">{value}</div>
            </div>
          ))}
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3">
          <div className="rounded-md bg-red-50 p-3" data-testid="change-before">
            <div className="text-xs font-semibold tracking-wide text-red-600 uppercase">Before</div>
            <div className="mt-1 text-sm break-words text-gray-900">{selected.before ?? '—'}</div>
          </div>
          <div className="rounded-md bg-emerald-50 p-3" data-testid="change-after">
            <div className="text-xs font-semibold tracking-wide text-emerald-700 uppercase">
              After
            </div>
            <div className="mt-1 text-sm break-words text-gray-900">{selected.after ?? '—'}</div>
          </div>
        </div>

        {/* NOT "this event cannot be edited or deleted" — that was false here. Migration
            0014 makes `audit_change_event` a rebuildable materialization with no REVOKE and
            no append-only trigger, precisely so it can be wiped and re-derived; the
            immutability lives one level down, in the FHIR version chain the row is
            reconstructed from. See AUDIT_INTEGRITY_STATEMENTS. */}
        <div className="mt-4 flex items-center gap-2 rounded-md bg-gray-50 p-3 text-xs text-gray-500">
          <IconLock size={14} className="shrink-0" />
          <span>
            <span className="font-medium text-gray-700">{AUDIT_INTEGRITY_STATEMENTS.changes}.</span>{' '}
            This row is reconstructed from the record&apos;s own version history, which is never
            rewritten.
          </span>
        </div>
      </div>
    </div>
  )
}
